/**
 * The sign-in attempt marker of a destination: the loop guard between a destination, the account hub and
 * the identity provider. It lives in sessionStorage, so it belongs to one tab and one destination origin,
 * and it never holds a token, only where the attempt is and when it started.
 *
 * - hub: this tab was sent to the hub to sign in (or to answer a reauthenticate requirement there).
 * - provider: back from the hub without a session here, the destination asked the identity provider to
 *   sign the person in through its SSO session (Auth0 only). Coming back once more without a session is
 *   a failed second attempt: the destination shows a recoverable error instead of trying again.
 * - silent: no session here, but this browser signed in to this destination before (remembered.ts), so the
 *   destination first asked the identity provider directly with prompt=none, skipping the hub. Any error
 *   coming back (no SSO session, MFA due, no longer a member) sends the person on the usual hub trip; it is
 *   never retried, because the trip writes the hub stage over it.
 */
export const SIGN_IN_ATTEMPT_KEY = 'trace.destination.signinAttempt';
/** A marker older than this is ignored: the attempt it records is over. */
export const SIGN_IN_ATTEMPT_MAX_AGE_MS = 5 * 60 * 1000;
// Clocks of one machine agree; a marker dated more than this in the future was not written by us.
const MAX_CLOCK_SKEW_MS = 60 * 1000;
const STAGES = ['hub', 'provider', 'silent'];
const REAUTHENTICATE_REASONS = [
    'sso_required',
    'mfa_required',
    'organization_login_required',
];
/** The tab's sessionStorage, or null where the browser refuses it (some privacy modes and sandboxes). */
export function defaultAttemptStorage() {
    try {
        return typeof window === 'undefined' ? null : window.sessionStorage;
    }
    catch {
        return null;
    }
}
/** The marker when it is well formed and recent, otherwise null. Storage failures read as no marker. */
export function readSignInAttempt(storage, now) {
    let raw;
    try {
        raw = storage.getItem(SIGN_IN_ATTEMPT_KEY);
    }
    catch {
        return null;
    }
    if (raw === null)
        return null;
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const record = value;
    const stage = STAGES.find((s) => s === record.stage);
    const at = record.at;
    if (!stage || typeof at !== 'number' || !Number.isFinite(at))
        return null;
    if (at > now + MAX_CLOCK_SKEW_MS || now - at >= SIGN_IN_ATTEMPT_MAX_AGE_MS)
        return null;
    if (record.reauthenticate === undefined)
        return { stage, at };
    const reauthenticate = REAUTHENTICATE_REASONS.find((r) => r === record.reauthenticate);
    return reauthenticate ? { stage, at, reauthenticate } : null;
}
/** Records the attempt. A storage failure is ignored: the destination then simply remembers nothing. */
export function writeSignInAttempt(storage, attempt) {
    try {
        storage.setItem(SIGN_IN_ATTEMPT_KEY, JSON.stringify(attempt));
    }
    catch {
        // Quota or privacy settings: nothing to do, the loop guard then sees no attempt.
    }
}
export function clearSignInAttempt(storage) {
    try {
        storage.removeItem(SIGN_IN_ATTEMPT_KEY);
    }
    catch {
        // Same as above.
    }
}
//# sourceMappingURL=attempts.js.map